import { type ReactNode, useEffect, useId, useState } from 'react'
import { cn } from '@/shared/lib'
import { Input } from '@/shared/ui/field'
import { matchPreset, type PeriodValue, PRESETS, periodValid, presetRange } from './presets'

interface PeriodPickerProps {
  value: PeriodValue
  onChange: (next: PeriodValue) => void
  className?: string
}

function PresetChip({
  active,
  expanded,
  onClick,
  children,
}: {
  active: boolean
  expanded?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      {...(expanded === undefined ? {} : { 'aria-expanded': expanded })}
      onClick={onClick}
      className={cn(
        'rounded-pill border px-3 pt-[5px] pb-[3px] font-mono text-xs uppercase tracking-wide transition-colors',
        active
          ? 'border-border-strong bg-bg-elevated text-text-primary'
          : 'border-border-subtle text-text-tertiary hover:text-text-secondary',
      )}
    >
      {children}
    </button>
  )
}

// Управляемый выбор периода (from/to в YYYY-MM-DD) для отчётов: пресеты-чипы +
// два нативных date-инпута. Ничего не знает про URL — только value/onChange.
// Валидацию to≥from показывает inline; наверх отдаёт даже невалидное значение
// (потребитель гасит запрос через enabled).
export function PeriodPicker({ value, onChange, className }: PeriodPickerProps) {
  const fromId = useId()
  const toId = useId()
  const errId = useId()
  const active = matchPreset(value)
  const invalid = !periodValid(value)

  // Даты руками — редкий случай: период почти всегда берут пресетом. Пара
  // date-инпутов под чипами занимала вертикали больше, чем сам список выходов,
  // поэтому раскрывается по «Произвольному» — и сама, когда пришедший период ни
  // в один пресет не попал.
  const [manualOpen, setManualOpen] = useState(false)
  const showRange = manualOpen || active === 'custom'

  // shared Input не форвардит ref (React 19, ref вешаем на нативные узлы) — фокус
  // «Произвольного» ведём по id, когда поля уже в DOM.
  useEffect(() => {
    if (manualOpen) document.getElementById(fromId)?.focus()
  }, [manualOpen, fromId])

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <fieldset aria-label="Быстрый период" className="m-0 flex flex-wrap gap-1.5 border-0 p-0">
        {PRESETS.map((p) => (
          <PresetChip
            key={p.kind}
            active={active === p.kind}
            onClick={() => {
              setManualOpen(false)
              onChange(presetRange(p.kind))
            }}
          >
            {p.label}
          </PresetChip>
        ))}
        {/* Произвольный — не диапазон, а метка «редактирую руками»: подсвечен,
            когда период не совпал с пресетами; клик раскрывает поля дат. */}
        <PresetChip
          active={active === 'custom'}
          expanded={showRange}
          onClick={() => setManualOpen((open) => !open)}
        >
          Произвольный
        </PresetChip>
      </fieldset>

      <div className={cn(showRange ? 'flex' : 'hidden', 'flex-wrap items-end gap-3')}>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={fromId} className="label">
            С
          </label>
          <Input
            id={fromId}
            type="date"
            value={value.from}
            max={value.to || undefined}
            onChange={(e) => onChange({ from: e.target.value, to: value.to })}
            className="w-auto [color-scheme:dark]"
            {...(invalid ? { 'aria-invalid': true, 'aria-describedby': errId } : {})}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor={toId} className="label">
            По
          </label>
          <Input
            id={toId}
            type="date"
            value={value.to}
            min={value.from || undefined}
            onChange={(e) => onChange({ from: value.from, to: e.target.value })}
            className="w-auto [color-scheme:dark]"
            {...(invalid ? { 'aria-invalid': true, 'aria-describedby': errId } : {})}
          />
        </div>
      </div>

      {invalid && (
        <p id={errId} role="alert" className="text-xs text-accent">
          Конец периода раньше начала
        </p>
      )}
    </div>
  )
}
